import { supabase } from "@/integrations/supabase/client";
import { isDomainBlacklisted, isSocialMediaDomain } from "@/utils/domainBlacklist";
import { toast } from "sonner";
import { supabaseAnalysisService } from "./supabaseAnalysisService";

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const analyzeSEO = async (
  url: string, 
  keyword: string, 
  leadEmail?: string,
  retryCount = 0
): Promise<any> => {
  if (!url || !keyword) {
    throw new Error("URL e palavra-chave são obrigatórios");
  }

  // Valida a URL
  try {
    new URL(url);
  } catch (error) {
    throw new Error("URL inválida");
  }

  if (isDomainBlacklisted(url)) {
    if (isSocialMediaDomain(url)) {
      throw new Error("Não é possível analisar páginas de redes sociais. Por favor, tente outro domínio.");
    } else {
      throw new Error("Não é possível analisar este domínio. Por favor, tente outro domínio.");
    }
  }

  const startTime = Date.now();
  let analysisId: string | null = null;
  let reportId: string | null = null;

  try {
    console.log(`🚀 Iniciando análise SEO (tentativa ${retryCount + 1}):`, { url, keyword, leadEmail });
    
    const { data, error } = await supabase.functions.invoke('analyze-seo', {
      body: {
        url: url.trim(),
        keyword: keyword.trim()
      }
    });

    if (error) {
      console.error("❌ Erro na edge function:", error);
      
      if (retryCount < MAX_RETRIES) {
        console.log(`🔄 Tentando novamente após erro, tentativa ${retryCount + 1}`);
        await sleep(RETRY_DELAY * (retryCount + 1));
        return analyzeSEO(url, keyword, leadEmail, retryCount + 1);
      }
      
      throw new Error(error.message || 'Erro ao analisar URL');
    }

    if (!data || !data.id) {
      throw new Error("Resposta inválida da API");
    }

    reportId = data.id;
    console.log('✅ Análise completada pela API, report_id:', reportId);

    // Registra o início da análise no banco (mesmo que seja após a conclusão)
    console.log('💾 Tentando registrar análise no Supabase...');
    analysisId = await supabaseAnalysisService.startAnalysis(
      reportId,
      url,
      keyword,
      leadEmail
    );

    if (!analysisId) {
      console.warn('⚠️ Não foi possível criar registro inicial, tentando registrar diretamente como concluído...');
    }

    // Calcula o tempo total da análise
    const endTime = Date.now();
    const totalTimeSeconds = (endTime - startTime) / 1000;

    // Obtém o score SEO se disponível
    const seoScore = data.analysis_result?.url_analyses?.[0]?.seo_score;

    console.log('📊 Dados da análise:', { reportId, seoScore, totalTimeSeconds });

    // Marca a análise como completada (ou cria um novo registro se necessário)
    const completed = await supabaseAnalysisService.completeAnalysis(
      reportId,
      seoScore,
      totalTimeSeconds
    );

    if (completed) {
      console.log('✅ Análise registrada no Supabase com sucesso');
    } else {
      console.warn('⚠️ Falha ao registrar análise no Supabase, mas a análise foi concluída');
    }

    return data;
  } catch (error) {
    console.error("❌ Erro em analyzeSEO:", error);
    
    // Se temos um report ID, tenta marcar como erro
    if (reportId) {
      console.log('📝 Tentando marcar análise como erro no banco...');
      await supabaseAnalysisService.markAnalysisAsError(
        reportId,
        error instanceof Error ? error.message : 'Erro desconhecido'
      );
    }
    
    if (retryCount < MAX_RETRIES) {
      console.log(`🔄 Tentando novamente após erro, tentativa ${retryCount + 1}`);
      await sleep(RETRY_DELAY * (retryCount + 1));
      return analyzeSEO(url, keyword, leadEmail, retryCount + 1);
    }
    throw error;
  }
};

export const saveAnalysis = (id: string, url: string, keyword: string, seoScore?: number) => {
  const recentAnalyses = JSON.parse(localStorage.getItem('recentAnalyses') || '[]');
  const newAnalysis = {
    id,
    url,
    keyword,
    timestamp: Date.now(),
    seo_score: seoScore
  };
  
  const updatedAnalyses = [newAnalysis, ...recentAnalyses]
    .filter((analysis, index, self) => 
      index === self.findIndex((t) => t.id === analysis.id)
    );

  localStorage.setItem('recentAnalyses', JSON.stringify(updatedAnalyses));
};
